import type { BombeoDetails, Muelle, ScaleEvent, SpecialEvent, SpecialEventType, StayOperation } from '../domain/scales'

export interface ScaleKey {
  year: number
  scaleId: string
}

export interface VesselDetails {
  vessel: string | null
  imo: string | null
  eslora: number | null
  bcm: number | null
  consignataria: string | null
  eta: string | null
  etd: string | null
  firstEta: string | null
  firstEtd: string | null
}

export interface ScaleRow extends ScaleKey, VesselDetails {
  isManual: boolean
  seenInSource: boolean
  finished: boolean
}

export interface StayDetails {
  fondeoPrevio: boolean | null
  anterior: string | null
  muellesAlternativos: string[]
  sostratTerminal: string | null
  sostratTermAdd: string | null
  sostratState: string | null
  operations: StayOperation[]
  ais: AisStayCheck | null
  actualStartTime: string | null
  actualEndTime: string | null
  actualsState: ActualsState | null
}

export type ActualsState = 'PENDING' | 'CONFIRMED' | 'UNCONFIRMED'

export const AIS_CHECK_STATUSES = ['MUELLE_DIFFERS', 'TIMES_DIFFER', 'AGREES', 'NOT_COMPARED'] as const
export type AisCheckStatus = (typeof AIS_CHECK_STATUSES)[number]

export interface AisStayCheck {
  status: AisCheckStatus | null
  arrival: string
  departure: string | null
  arrivalDifferenceS: number | null
  departureDifferenceS: number | null
  arrivalObserved: boolean | null
  muelle: Muelle | null
  muelleAgrees: boolean | null
}

export const MARPOL_ANNEXES = ['I', 'V'] as const
export type MarpolAnnex = (typeof MARPOL_ANNEXES)[number]

interface MarpolDetails {
  marpolAnnex: MarpolAnnex | null
  marpolNoticeAt: string | null
}

export type EventDetails = StayDetails & BombeoDetails & MarpolDetails

export const NO_EVENT_DETAILS: EventDetails = {
  fondeoPrevio: null,
  anterior: null,
  muellesAlternativos: [],
  sostratTerminal: null,
  sostratTermAdd: null,
  sostratState: null,
  operations: [],
  ais: null,
  actualStartTime: null,
  actualEndTime: null,
  actualsState: null,
  connectionPoint: null,
  productClass: null,
  product: null,
  quantityT: null,
  operationKind: null,
  marpolAnnex: null,
  marpolNoticeAt: null,
}

export type EventRow = ScaleEvent & { year: number; terminals: string[] } & EventDetails

export interface ScaleWithEventRows {
  scale: ScaleRow
  events: EventRow[]
}

export interface SpecialEventInput extends BombeoDetails, MarpolDetails {
  type: SpecialEventType
  startTime: string
  endTime: string | null
}

export interface ConnectionPoint {
  code: string
  muelle: Muelle
  terminal: string
  status: 'IN_SERVICE' | 'OUT_OF_SERVICE' | 'UNLABELLED'
  note: string | null
}

const SOURCE_RUN_SOURCES = ['SOSTRAT', 'AIS'] as const
export type SourceRunSource = (typeof SOURCE_RUN_SOURCES)[number]

const SOURCE_RUN_STATUSES = ['REQUESTED', 'RUNNING', 'SUCCEEDED', 'FAILED'] as const
export type SourceRunStatus = (typeof SOURCE_RUN_STATUSES)[number]
type SourceRunTrigger = 'MANUAL' | 'SCHEDULE'

export interface SourceRun {
  id: string
  source: SourceRunSource
  trigger: SourceRunTrigger
  status: SourceRunStatus
  requestedAt: string
  startedAt: string | null
  finishedAt: string | null
  counts: Record<string, number>
  problems: string[]
}

export const isRunInProgress = (run: Pick<SourceRun, 'status'>): boolean => run.status === 'REQUESTED' || run.status === 'RUNNING'

export interface SyncStart {
  kind: 'started' | 'busy'
  run: Pick<SourceRun, 'id' | 'requestedAt'>
}

export type SourceErrorCode =
  | 'NOT_FOUND'
  | 'END_BEFORE_START'
  | 'NOT_SPECIAL'
  | 'SCALE_CLOSED'
  | 'NO_STAYS'
  | 'START_OUTSIDE_STAYS'
  | 'SYNC_REFUSED'

export class SourceError extends Error {
  constructor(
    readonly code: SourceErrorCode,
    message: string = code,
  ) {
    super(message)
    this.name = 'SourceError'
  }
}

export interface ScalesSource {
  listScales: (options: { finished?: boolean }) => Promise<ScaleWithEventRows[]>
  getScale: (key: ScaleKey) => Promise<ScaleWithEventRows | undefined>
  getEvent: (id: string) => Promise<EventRow | undefined>
  createSpecialEvent: (key: ScaleKey, input: SpecialEventInput) => Promise<EventRow & SpecialEvent>
  updateSpecialEvent: (id: string, input: SpecialEventInput) => Promise<EventRow & SpecialEvent>
  setFinished: (key: ScaleKey, finished: boolean) => Promise<ScaleRow>
  listConnectionPoints: () => Promise<ConnectionPoint[]>
  startRead: (source: SourceRunSource) => Promise<SyncStart>
  latestRun: (source: SourceRunSource) => Promise<SourceRun | undefined>
  getSourceRun: (id: string) => Promise<SourceRun | undefined>
}
